<template>
  <div class="certiciateorder">

    <h1 id="formTitle">Letter of no record of marriage application form</h1>
    <div>
      <p>Use this application form to apply for a letter of no record of marriage if you are getting married overseas and
        need evidence of your marriage status in Queensland.</p>
      <p>It will take you about 10 minutes to complete this application form.</p>
      <h2>Step 1—Fill out online form</h2>
      <ol>
        <li>Answer the questions below. </li>
      </ol>
      <h2>Step 2—Complete on paper and submit</h2>
      <ol start="2">
        <li>Get your <a class="help lightbox"
            href="https://www.qld.gov.au/law/births-deaths-marriages-and-divorces/requirements#proof-of-id"
            target="_blank" id="lightbox-uid-0">proof of ID</a> and supporting documents.</li>
      </ol>
      <h2>Step 3—What next?</h2>
      <ol start="3">
        <li>Check with the <a
            href="http://www.dfat.gov.au/about-us/Pages/foreign-embassies-and-consulates-in-australia.aspx"
            target="_blank" title="Opens in new window">consulate of the country <span
              class="qg-blank-notice sr-only">(Opens in new window)</span> </a> where you plan to get married, to find out
          if you need to get the letter that we provide you legalised with an <a href="http://www.apostille.com.au/"
            target="_blank" title="Opens in new window">apostille stamp <span class="qg-blank-notice sr-only">(Opens in
              new window)</span> </a>.</li>
      </ol>
    </div>
    <form @submit="submitForm" id="submitForm">
      <ol class="questions">
        <li>
          <fieldset>
            <legend>
              <span class="h3">Can I search for marriages in Queensland?</span>
            </legend>
            <ol class="questions">
              <li>
                <fieldset>
                  <legend>
                    <span class="label">Are you the person who the search is for?</span>
                    <abbr class="required" title="(required)">*</abbr>
                  </legend>
                  <ul class="choices compact">
                    <li>
                      <input v-model="form.personWhoTheSearchIsFor" id="are-you-the-person-who-the-search-is-for-yes"
                        value="yes" name="are-you-the-person-who-the-search-is-for" required="" type="radio" />
                      <label for="are-you-the-person-who-the-search-is-for-yes">Yes</label>
                    </li>
                    <li>
                      <input v-model="form.personWhoTheSearchIsFor" id="are-you-the-person-who-the-search-is-for-no"
                        value="no" name="are-you-the-person-who-the-search-is-for" required="" type="radio" />
                      <label for="are-you-the-person-who-the-search-is-for-no">No</label>
                    </li>
                    <li>
                      <input v-model="form.personWhoTheSearchIsFor"
                        id="are-you-the-person-who-the-search-is-for-no-i-have-authority-and-proof-of-id-from-the-person"
                        value="noWithAuthority" name="are-you-the-person-who-the-search-is-for" required=""
                        type="radio" />
                      <label
                        for="are-you-the-person-who-the-search-is-for-no-i-have-authority-and-proof-of-id-from-the-person">No,
                        I have authority and proof of ID from the person</label>
                    </li>
                  </ul>
                </fieldset>
              </li>
              <li id="poi"
                v-if="form.personWhoTheSearchIsFor == 'noWithAuthority' || form.personWhoTheSearchIsFor == 'yes'">
                <fieldset>
                  <legend>
                    <span class="label">Do you have all the proof you need for identification?</span>
                    <abbr class="required" title="(required)">*</abbr>
                    <small class="hint">
                      <a class="help lightbox"
                        href="https://www.qld.gov.au/law/births-deaths-marriages-and-divorces/marriage-weddings-and-civil-partnerships/proof-of-id#proof-of-id"
                        id="lightbox-uid-2">View the proof you will need to provide</a>
                    </small>
                  </legend>
                  <ul class="choices compact">
                    <li>
                      <input v-model="eligibility.hasPoi" id="poi-yes" value="true" name="poi" type="radio"><label
                        for="poi-yes">Yes</label>
                    </li>
                    <li>
                      <input v-model="eligibility.hasPoi" id="poi-no" value="false" name="poi" type="radio"><label
                        for="poi-no">No</label>
                    </li>
                  </ul>
                </fieldset>
                <fieldset>
                  <ul class="questions">
                    <li v-if="eligibility.hasPoi == 'true'">
                      <input v-model="eligibility.contactMe" id="contact-me" value="true" type="checkbox"
                        name="contact-me" required="" />
                      <label for="contact-me">
                        By using this online service and providing an email address and mobile number, I consent to the
                        use of that email address and mobile number for the Registry of Births, Deaths and Marriages
                        (RBDM) to provide me with
                        electronic information and for contact using SMS and email that relates to this application.
                        I understand that it is my responsibility to ensure that I have nominated a secure email address
                        for RBDM to send correspondence to.
                        I acknowledge that it is my responsibility to ensure the security of that information upon receipt
                        of it.
                      </label>
                    </li>
                  </ul>
                </fieldset>
              </li>

            </ol>
          </fieldset>
        </li>
        <li
          v-if="eligibility.contactMe && eligibility.hasPoi == 'true' && (form.personWhoTheSearchIsFor == 'noWithAuthority' || form.personWhoTheSearchIsFor == 'yes')">
          <fieldset>
            <legend>
              <span class="h2">Fill in your application form</span>
            </legend>
            <ol class="questions">
              <li>
                <fieldset>
                  <legend>
                    <span class="h3">Marriage search details</span>
                  </legend>
                  <div class="status info" id="marriage-search-instructions">
                    <span class="label">You will need to tell us the search details in your completed application
                      form.</span>
                    <br />
                    <br />
                    <span class="label">The type of searches done are from either:</span>

                    <ol class="disc-list">
                      <li>when you turned 16 to now</li>
                      <li>if you are divorced—from the date of your divorce to now</li>
                      <li>if your previous spouse passed away—from their date of death to now</li>
                      <li>if you have moved to live in Queensland—from the date you moved to Queensland to now.</li>
                    </ol>
                  </div>
                </fieldset>
              </li>
              <li class="atomic group">
                <fieldset>
                  <legend>
                    <span class="label">Period of years to be searched</span>
                  </legend>
                  <ol class="questions compact">
                    <li>
                      <label for="search-from"><span class="label">From </span><abbr class="required"
                          title="(required)">*</abbr><small>dd/mm/yyyy</small> </label>
                      <input v-model="form.searchFrom" id="search-from" maxlength="10" required="required"
                        name="search-from">
                    </li>
                    <li>
                      <label for="search-to"><span class="label">To </span><abbr class="required"
                          title="(required)">*</abbr><small>dd/mm/yyyy</small> </label>
                      <input v-model="form.searchTo" id="search-to" maxlength="10" required="required" name="search-to">
                    </li>
                  </ol>
                </fieldset>
              </li>
              <li>
                <label for="reason-document-is-required">
                  <span class="label">What is the reason you want a letter of no record of marriage?</span>
                  <abbr class="required" title="(required)">*</abbr>
                </label>
                <textarea v-model="form.reasonDocumentRequired" id="reason-document-is-required" maxlength="50" cols="40"
                  required="required" name="reason-document-is-required"></textarea>
              </li>
              <li>
                <fieldset>
                  <legend>
                    <span class="label">Have you been married before?</span>
                    <abbr class="required" title="(required)">*</abbr>
                  </legend>
                  <ul class="choices compact">
                    <li>
                      <input v-model="form.marriedBefore" id="have-you-been-married-before-yes" value="true"
                        required="required" name="have-you-been-married-before" type="radio"><label
                        for="have-you-been-married-before-yes">Yes</label>
                    </li>
                    <li>
                      <input v-model="form.marriedBefore" id="have-you-been-married-before-no" value="false"
                        required="required" name="have-you-been-married-before" type="radio"><label
                        for="have-you-been-married-before-no">No</label>
                    </li>
                  </ul>
                </fieldset>
              </li>
              <li v-if="form.marriedBefore == 'true'" class="section">
                <fieldset>
                  <legend>
                    <span class="label">Date your most recent marriage ended</span>
                  </legend>
                  <ol class="questions">
                    <li>
                      <label for="date-your-most-recent-marriage-ended">
                        <abbr class="required" title="(required)">*</abbr>
                        <small class="hint">dd/mm/yyyy</small>
                      </label>
                      <input id="date-your-most-recent-marriage-ended" maxlength="10" required="required"
                        name="date-your-most-recent-marriage-ended" type="date" />
                    </li>
                    <li>
                      <div class="status info">
                        <p>Supported file types: PDF, JPEG, PNG. </p>
                        <br />
                        <br />
                        <p>The maximum file size is 20 MB. </p>
                      </div>
                    </li>
                    <li>
                      <fieldset>
                        <legend>
                          <span class="label">Upload a copy of your evidence of divorce or a death certificate</span>
                          <abbr class="required" title="(required)">*</abbr>
                        </legend>
                        <input id="evidenceOfDivorceOrDeathCertificate" @change="handleUpload" type="file"
                          name="evidenceOfDivorceOrDeathCertificate" required="true" :accept="uploads.acceptedTypes">
                      </fieldset>
                    </li>
                    <li class="section">
                      <fieldset>
                        <legend>
                          <span class="label">How did your most recent marriage end?</span>
                          <abbr class="required" title="(required)">*</abbr>
                        </legend>
                        <ul class="choices">
                          <li>
                            <input id="how-did-your-most-recent-marriage-end-divorce" value="Divorce"
                              name="how-did-your-most-recent-marriage-end" required="" type="radio"><label
                              for="how-did-your-most-recent-marriage-end-divorce">Divorce</label>
                          </li>
                          <li>
                            <input id="how-did-your-most-recent-marriage-end-death" value="Death"
                              name="how-did-your-most-recent-marriage-end" required="" type="radio"><label
                              for="how-did-your-most-recent-marriage-end-death">Death</label>
                          </li>
                          <li>
                            <input id="how-did-your-most-recent-marriage-end-annulment" value="Annulment"
                              name="how-did-your-most-recent-marriage-end" required="" type="radio"><label
                              for="how-did-your-most-recent-marriage-end-annulment">Annulment</label>
                          </li>
                        </ul>
                      </fieldset>
                    </li>
                  </ol>
                </fieldset>
              </li>
              <li>
                <fieldset>
                  <legend>
                    <span class="label">Have you used other names?</span>
                    <abbr class="required" title="(required)">*</abbr>
                  </legend>
                  <ul class="choices compact">
                    <li>
                      <input v-model="form.usedOtherNames" id="have-you-used-other-names-yes" value="true"
                        required="required" name="have-you-used-other-names" type="radio"><label
                        for="have-you-used-other-names-yes">Yes</label>
                    </li>
                    <li>
                      <input v-model="form.usedOtherNames" id="have-you-used-other-names-no" value="false"
                        required="required" name="have-you-used-other-names" type="radio"><label
                        for="have-you-used-other-names-no">No</label>
                    </li>
                  </ul>
                </fieldset>
              </li>
              <li>
                <div class="status warn" id="marriage-search-instructions">
                  The search will be made under your name at birth if you do not provide more information.
                </div>
              </li>
              <li v-if="form.usedOtherNames == 'true'">
                <fieldset>
                  <legend>
                    <span class="label">Does a search need to be made under the other name?</span>
                    <small class="hint">There is a cost of $28.95 for each additional name searched to be paid when the
                      application is made.</small>
                    <abbr class="required" title="(required)">*</abbr>
                    <small class="hint relevance">(If you chose ‘Yes’ above)</small>
                  </legend>
                  <ul class="choices compact">
                    <li>
                      <input v-model="form.searchUnderOtherName" id="does-a-search-need-to-be-made-under-this-name-yes"
                        value="true" name="does-a-search-need-to-be-made-under-this-name" required="" onclick="check();"
                        type="radio"><label for="does-a-search-need-to-be-made-under-this-name-yes">Yes</label>
                    </li>
                    <li>
                      <input v-model="form.searchUnderOtherName" id="does-a-search-need-to-be-made-under-this-name-no"
                        value="false" name="does-a-search-need-to-be-made-under-this-name" required="" type="radio"><label
                        for="does-a-search-need-to-be-made-under-this-name-no">No</label>
                    </li>
                  </ul>
                </fieldset>
              </li>
              <li class="section" v-if="form.usedOtherNames == 'true' && form.searchUnderOtherName == 'true'">
                <fieldset>
                  <legend>
                    <span class="label">Additional search details</span>
                  </legend>
                  <ol class="questions">
                    <li class="atomic group">
                      <fieldset>
                        <legend>
                          <span class="label">Period of years to be searched</span>
                        </legend>
                        <ol class="questions compact">
                          <li>
                            <label for="additional-search-from"><span class="label">From </span><abbr class="required"
                                title="(required)">*</abbr><small>dd/mm/yyyy</small></label>
                            <input id="additional-search-from" maxlength="10" name="additional-search-from">
                          </li>
                          <li>
                            <label for="additional-search-to"><span class="label">To </span><abbr class="required"
                                title="(required)">*</abbr><small>dd/mm/yyyy</small></label>
                            <input id="additional-search-to" maxlength="10" name="additional-search-to">
                          </li>
                        </ol>
                      </fieldset>
                    </li>
                    <li>
                      <ol class="questions compact">
                        <li>
                          <label for="search-previous-first-names">
                            <span class="label">Previous first name</span>
                            <abbr class="required" title="(required)">*</abbr>
                          </label>
                          <input id="search-previous-first-names" maxlength="125" name="search-previous-first-names" />
                        </li>
                        <li>
                          <label for="search-previous-middle-names">
                            <span class="label">Previous middle name(s)</span>
                          </label>
                          <input id="search-previous-middle-names" maxlength="130" name="search-previous-middle-names">
                        </li>
                        <li>
                          <label for="search-previous-family-name">
                            <span class="label">Previous family name</span>
                            <abbr class="required" title="(required)">*</abbr>
                          </label>
                          <input id="search-previous-family-name" maxlength="80" name="search-previous-family-name">
                        </li>
                      </ol>
                    </li>
                  </ol>
                </fieldset>
              </li>
              <li class="section">
                <fieldset>
                  <legend>
                    <span class="h2">Birth details</span>
                  </legend>
                  <ol class="questions">
                    <li class="atomic group">
                      <fieldset>
                        <legend>
                          <span class="label">Place of birth</span>
                        </legend>
                        <ol class="questions compact">
                          <li>
                            <label for="place-of-birth">
                              <span class="label">Town or city</span>
                              <abbr class="required" title="(required)">*</abbr>
                            </label>
                            <input v-model="form.placeOfBirthTown" id="place-of-birth" maxlength="85" required="required"
                              name="place-of-birth">
                          </li>
                          <li>
                            <label for="place-of-birth-state">
                              <span class="label">State or territory</span>
                              <abbr class="required" title="(required)">*</abbr>
                            </label>
                            <input v-model="form.placeOfBirthState" id="place-of-birth-state" maxlength="85"
                              required="required" name="place-of-birth-state">
                          </li>
                          <li>
                            <label for="country-of-birth">
                              <span class="label">Country</span>
                              <abbr class="required" title="(required)">*</abbr>
                            </label>
                            <input v-model="form.placeOfBirthCountry" id="country-of-birth" maxlength="85"
                              required="required" name="country-of-birth">
                          </li>
                        </ol>
                      </fieldset>
                    </li>
                    <li>
                      <label for="date-of-birth2">
                        <span class="label">Date of birth</span>
                        <abbr class="required" title="(required)">*</abbr>
                        <small class="hint">dd/mm/yyyy</small>
                      </label>
                      <input v-model="form.dateOfBirth" id="date-of-birth2" maxlength="10" required="required"
                        name="date-of-birth" placeholder="" type="text">
                    </li>
                    <li class="atomic group">
                      <fieldset>
                        <legend>
                          <span class="label">Name (at time of your birth)</span>
                        </legend>
                        <ol class="questions compact">
                          <li>
                            <label for="birth-first-names">
                              <span class="label">First name</span>
                              <abbr class="required" title="(required)">*</abbr>
                            </label>
                            <input v-model="form.birthFirstName" id="birth-first-names" maxlength="125"
                              required="required" name="birth-first-names">
                          </li>
                          <li>
                            <label for="birth-middle-names">
                              <span class="label">Middle name(s)</span>
                            </label>
                            <input v-model="form.birthMiddleName" id="birth-middle-names" maxlength="130"
                              name="birth-middle-names">
                          </li>
                          <li>
                            <label for="birth-family-name">
                              <span class="label">Family name</span>
                              <abbr class="required" title="(required)">*</abbr>
                            </label>
                            <input v-model="form.birthFamilyName" id="birth-family-name" maxlength="80"
                              required="required" name="birth-family-name">
                          </li>
                        </ol>
                      </fieldset>
                    </li>
                    <li>
                      <fieldset>
                        <legend>
                          <span class="label">Sex</span>
                          <abbr class="required" title="(required)">*</abbr>
                        </legend>
                        <ul class="choices compact">
                          <li>
                            <input v-model="form.birthSex" id="gender-female" value="female" required="required"
                              name="gender" type="radio">
                            <label for="gender-female">Female</label>
                          </li>
                          <li>
                            <input v-model="form.birthSex" id="gender-male" value="male" required="required" name="gender"
                              type="radio">
                            <label for="gender-male">Male</label>
                          </li>
                        </ul>
                      </fieldset>
                    </li>
                    <li>
                      <div class="status info" id="parent-instructions">
                        <p>It is important that parent names are completed.</p>
                        <p>Your parent details help us to do the search accurately but will not be included on the letter
                          of no record of marriage in Queensland.</p>
                      </div>
                    </li>
                    <li class="atomic group">
                      <fieldset>
                        <legend>
                          <span class="label">Mother's name (as shown on your birth certificate)</span>
                        </legend>
                        <ol class="questions compact">
                          <li>
                            <label for="mothers-first-names">
                              <span class="label">First name</span>
                              <abbr class="required" title="(required)">*</abbr>
                            </label>
                            <input v-model="form.motherFirstName" id="mothers-first-names" maxlength="125"
                              required="required" name="mothers-first-names">
                          </li>
                          <li>
                            <label for="mothers-middle-names">
                              <span class="label">Middle name(s)</span>
                            </label>
                            <input v-model="form.motherMiddleName" id="mothers-middle-names" maxlength="130"
                              name="mothers-middle-names">
                          </li>
                          <li>
                            <label for="mothers-maiden-surname">
                              <span class="label">Family name at birth</span>
                              <abbr class="required" title="(required)">*</abbr>
                              <small class="hint">This is the mother's family name at the time of her birth. If the mother
                                didn't have a family name at birth, enter n/a</small>
                            </label>
                            <input v-model="form.motherFamilyName" id="mothers-maiden-surname" maxlength="80"
                              required="required" name="mothers-maiden-surname">
                          </li>
                        </ol>
                      </fieldset>
                    </li>
                    <li class="atomic group">
                      <fieldset>
                        <legend>
                          <span class="label">Father's/parent's name (as shown on your birth certificate)</span>
                          <small class="hint">If no father/parent is on your birth certificate enter n/a</small>
                        </legend>
                        <ol class="questions compact">
                          <li>
                            <label for="fathers-parents-first-names">
                              <span class="label">First name</span>
                              <abbr class="required" title="(required)">*</abbr>
                            </label>
                            <input v-model="form.parentFirstName" id="fathers-parents-first-names" maxlength="125"
                              required="required" name="fathers-parents-first-names">
                          </li>
                          <li>
                            <label for="fathers-parents-middle-names">
                              <span class="label">Middle name(s)</span>
                            </label>
                            <input v-model="form.parentMiddleName" id="fathers-parents-middle-names" maxlength="130"
                              name="fathers-parents-middle-names">
                          </li>
                          <li>
                            <label for="fathers-parents-family-name">
                              <span class="label">Family name</span>
                              <abbr class="required" title="(required)">*</abbr>
                            </label>
                            <input v-model="form.parentFamilyName" id="fathers-parents-family-name" maxlength="80"
                              required="required" name="fathers-parents-family-name">
                          </li>
                        </ol>
                      </fieldset>
                    </li>
                  </ol>
                </fieldset>
              </li>
              <li class="section">
                <fieldset>
                  <legend>
                    <span class="h2">Who is applying</span>
                  </legend>
                  <ol class="questions">
                    <li class="atomic group">
                      <fieldset>
                        <legend>
                          <span class="label">Your relationship to the person named in the marriage search details</span>
                          <abbr class="required" title="(required)">*</abbr>
                        </legend>
                        <ul class="choices">
                          <li>
                            <input v-model="form.selfApplicant" id="applicants-relationship-to-the-person-self"
                              value="true" required="required" name="applicants-relationship-to-the-person" type="radio">
                            <label for="applicants-relationship-to-the-person-self">Self</label>
                          </li>
                          <li>
                            <input v-model="form.selfApplicant"
                              id="applicants-relationship-to-the-person-other-please-specify" value="false"
                              required="required" name="applicants-relationship-to-the-person" type="radio">
                            <label for="applicants-relationship-to-the-person-other-please-specify">Other (please
                              specify)</label>
                          </li>
                        </ul>
                      </fieldset>
                    </li>
                    <li v-if="form.selfApplicant == 'false'" class="section">
                      <fieldset>
                        <legend>
                          <span class="label">Other relationship</span>
                        </legend>
                        <ol class="questions">
                          <li>
                            <label for="other-relationship2">
                              <abbr class="required" title="(required)">*</abbr>
                              <small class="hint">Parent, brother, sister (additional eligibility evidence
                                required)</small>
                            </label>
                            <input id="other-relationship2" maxlength="50" name="other-relationship">
                          </li>
                          <li>
                            <div class="status warn">
                              <label for="applicant-details-tnc">If this application relates to a person other than
                                yourself, written authority and proof of ID from the person concerned is required in
                                addition to proof of your own identity. </label>
                            </div>
                            <div class="status info">
                              <p>Supported file types: PDF, JPEG, PNG. </p>
                              <br />
                              <br />
                              <p>The maximum file size is 20 MB. </p>
                            </div>
                          </li>
                          <li>
                            <fieldset>
                              <legend>
                                <span class="label">Upload a copy of your written authority and proof of ID from the
                                  person concerned</span>
                                <abbr class="required" title="(required)">*</abbr>
                              </legend>
                              <input id="writtenAuthorityAndProofOfId" @change="handleUpload" type="file"
                                name="writtenAuthorityAndProofOfId" required="true" :accept="uploads.acceptedTypes">
                            </fieldset>
                          </li>
                        </ol>
                      </fieldset>
                    </li>
                    <li class="atomic group">
                      <fieldset>
                        <legend>
                          <span class="label">Your name</span>
                          <small class="hint">As shown on your proof of ID</small>
                        </legend>
                        <ol class="questions compact">
                          <li>
                            <label for="applicants-first-names">
                              <span class="label">First name</span>
                              <abbr class="required" title="(required)">*</abbr>
                            </label>
                            <input id="applicants-first-names" maxlength="125" required="required"
                              name="applicants-first-names">
                          </li>
                          <li>
                            <label for="applicants-middle-names">
                              <span class="label">Middle name(s)</span>
                            </label>
                            <input id="applicants-middle-names" maxlength="130" name="applicants-middle-names">
                          </li>
                          <li>
                            <label for="applicants-family-name">
                              <span class="label">Family name</span>
                              <abbr class="required" title="(required)">*</abbr>
                            </label>
                            <input id="applicants-family-name" maxlength="80" required="required"
                              name="applicants-family-name">
                          </li>
                        </ol>
                      </fieldset>
                    </li>
                    <li class="atomic group">
                      <fieldset>
                        <legend>
                          <span class="label">Your home address</span>
                        </legend>
                        <ol class="questions">
                          <li>
                            <label for="applicants-address-line-4">
                              <span class="label">Address line 1</span>
                              <abbr class="required" title="(required)">*</abbr>
                            </label>
                            <input id="applicants-address-line-4" maxlength="25" required="required"
                              name="applicants-address-line-1">
                          </li>
                          <li>
                            <label for="applicants-address-line-5">
                              <span class="label">Address line 2</span>
                            </label>
                            <input id="applicants-address-line-5" maxlength="25" name="applicants-address-line-2">
                          </li>
                          <li>
                            <label for="applicants-address-line-6">
                              <span class="label">Address line 3</span>
                            </label>
                            <input id="applicants-address-line-6" maxlength="25" name="applicants-address-line-3">
                          </li>
                          <li class="group">
                            <ol class="questions compact">
                              <li>
                                <label for="applicants-town2">
                                  <span class="label">Town, city or suburb</span>
                                  <abbr class="required" title="(required)">*</abbr>
                                </label>
                                <input id="applicants-town2" maxlength="25" required="required" name="applicants-town">
                              </li>
                              <li>
                                <label for="applicants-state2">
                                  <span class="label">State or territory</span>
                                  <abbr class="required" title="(required)">*</abbr>
                                </label>
                                <input id="applicants-state2" maxlength="25" required="required" name="applicants-state">
                              </li>
                              <li>
                                <label for="applicants-postcode2">
                                  <span class="label">Postcode</span>
                                  <abbr class="required" title="(required)">*</abbr>
                                </label>
                                <input id="applicants-postcode2" maxlength="10" required="required"
                                  name="applicants-postcode">
                              </li>
                            </ol>
                          </li>
                          <li>
                            <label for="applicants-country">
                              <span class="label">Country</span>
                            </label>
                            <input id="applicants-country" maxlength="30" name="applicants-country">
                          </li>
                        </ol>
                      </fieldset>
                    </li>
                    <li>
                      <fieldset>
                        <legend>
                          <span class="label">Are the postal details different to the address provided above?</span>
                          <abbr class="required" title="(required)">*</abbr>
                        </legend>
                        <ul class="choices compact">
                          <li>
                            <input v-model="eligibility.postalAddressDifferentToResidence"
                              id="is-your-postal-address-different-to-the-address-provided-above-yes" value="true"
                              required="required" name="is-your-postal-address-different-to-the-address-provided-above"
                              type="radio">
                            <label for="is-your-postal-address-different-to-the-address-provided-above-yes">Yes</label>
                          </li>
                          <li>
                            <input v-model="eligibility.postalAddressDifferentToResidence"
                              id="is-your-postal-address-different-to-the-address-provided-above-no" value="false"
                              required="required" name="is-your-postal-address-different-to-the-address-provided-above"
                              type="radio">
                            <label for="is-your-postal-address-different-to-the-address-provided-above-no">No</label>
                          </li>
                        </ul>
                      </fieldset>
                    </li>
                    <li v-if="eligibility.postalAddressDifferentToResidence == 'true'" class="section">
                      <fieldset>
                        <legend>
                          <span class="h3">Postal details</span>
                        </legend>
                        <ol class="questions">
                          <li>
                            <fieldset>
                              <legend>
                                <span class="label">Do you want the letter posted to a different person?</span>
                                <abbr class="required" title="(required)">*</abbr>
                              </legend>
                              <ul class="choices compact">
                                <li>
                                  <input v-model="eligibility.postedToDifferentPerson"
                                    id="do-you-want-the-letter-posted-to-a-different-person-yes" value="true"
                                    required="required" name="do-you-want-the-letter-posted-to-a-different-person"
                                    type="radio">
                                  <label for="do-you-want-the-letter-posted-to-a-different-person-yes">Yes</label>
                                </li>
                                <li>
                                  <input v-model="eligibility.postedToDifferentPerson"
                                    id="do-you-want-the-letter-posted-to-a-different-person-no" value="false"
                                    required="required" name="do-you-want-the-letter-posted-to-a-different-person"
                                    type="radio">
                                  <label for="do-you-want-the-letter-posted-to-a-different-person-no">No</label>
                                </li>
                              </ul>
                            </fieldset>
                          </li>
                          <li v-if="eligibility.postedToDifferentPerson == 'true'" class="atomic group" id="postal-name">
                            <fieldset>
                              <legend>
                                <span class="label">Name</span>
                              </legend>
                              <ol class="questions compact">
                                <li>
                                  <label for="postal-first-names">
                                    <span class="label">First name</span>
                                    <abbr class="required" title="(required)">*</abbr>
                                  </label>
                                  <input id="postal-first-names" maxlength="125" name="postal-first-names">
                                </li>
                                <li>
                                  <label for="postal-middle-names">
                                    <span class="label">Middle name(s)</span>
                                  </label>
                                  <input id="postal-middle-names" maxlength="130" name="postal-middle-names">
                                </li>
                                <li>
                                  <label for="postal-family-name">
                                    <span class="label">Family name</span>
                                    <abbr class="required" title="(required)">*</abbr>
                                  </label>
                                  <input id="postal-family-name" maxlength="80" name="postal-family-name">
                                </li>
                              </ol>
                            </fieldset>
                          </li>
                          <li class="atomic group">
                            <fieldset>
                              <legend>
                                <span class="label">Postal address</span>
                              </legend>
                              <ol class="questions">
                                <li>
                                  <label for="postal-address-line-1">
                                    <span class="label">Address line 1</span>
                                    <abbr class="required" title="(required)">*</abbr>
                                  </label>
                                  <input id="postal-address-line-1" maxlength="25" name="postal-address-line-1">
                                </li>
                                <li>
                                  <label for="postal-address-line-2">
                                    <span class="label">Address line 2</span>
                                  </label>
                                  <input id="postal-address-line-2" maxlength="25" name="postal-address-line-2">
                                </li>
                                <li>
                                  <label for="postal-address-line-3">
                                    <span class="label">Address line 3</span>
                                  </label>
                                  <input id="postal-address-line-3" maxlength="25" name="postal-address-line-3">
                                </li>
                                <li class="group">
                                  <ol class="questions compact">
                                    <li>
                                      <label for="postal-address-town">
                                        <span class="label">Town, city or suburb</span>
                                        <abbr class="required" title="(required)">*</abbr>
                                      </label>
                                      <input id="postal-address-town" maxlength="25" name="postal-address-town">
                                    </li>
                                    <li>
                                      <label for="postal-address-state">
                                        <span class="label">State or territory</span>
                                        <abbr class="required" title="(required)">*</abbr>
                                      </label>
                                      <input id="postal-address-state" maxlength="25" name="postal-address-state">
                                    </li>
                                    <li>
                                      <label for="postal-address-postcode">
                                        <span class="label">Postcode</span>
                                        <abbr class="required" title="(required)">*</abbr>
                                      </label>
                                      <input id="postal-address-postcode" maxlength="10" name="postal-address-postcode">
                                    </li>
                                  </ol>
                                </li>
                                <li>
                                  <label for="postal-address-country">
                                    <span class="label">Country</span>
                                  </label>
                                  <input id="postal-address-country" maxlength="30" name="postal-address-country">
                                </li>
                              </ol>
                            </fieldset>
                          </li>
                        </ol>
                      </fieldset>
                    </li>
                  </ol>
                </fieldset>
              </li>
            </ol>
            <ol class="questions">
              <li>
                <fieldset>
                  <legend>
                    <span class="h3">Upload your proof of identity</span>
                    <div class="status info">
                      <p>Supported file types: PDF, JPEG, PNG. </p>
                      <br />
                      <br />
                      <p>The maximum file size is 20 MB. </p>
                    </div>
                  </legend>

                  <ul class="questions">
                    <li>
                      <label for="proofOfIdentity">
                        <span class="h4 label">Proof of identity (1)</span>
                        <abbr class="required" title="(required)">*</abbr>
                      </label>
                      <input id="proofOfIdentity1" @change="handleUpload" type="file" name="proofOfIdentity1"
                        required="true" :accept="uploads.acceptedTypes">
                    </li>
                    <li>
                      <label for="proofOfIdentity">
                        <span class="h4 label">Proof of identity (2)</span>
                        <abbr class="required" title="(required)">*</abbr>
                      </label>
                      <input id="proofOfIdentity2" @change="handleUpload" type="file" name="proofOfIdentity2"
                        required="true" :accept="uploads.acceptedTypes">
                    </li>
                    <li>
                      <label for="proofOfIdentity3">
                        <span class="h4 label">Proof of identity (3)</span>
                        <abbr class="required" title="(required)">*</abbr>
                      </label>
                      <input id="proofOfIdentity3" @change="handleUpload" type="file" name="proofOfIdentity3"
                        required="true" :accept="uploads.acceptedTypes">
                    </li>
                  </ul>
                </fieldset>
              </li>
              <li>
                <fieldset>
                  <ul class="questions compact">
                    <li class="applicantPreferredContactEmail">
                      <label for="applicantPreferredContactEmail"> <span class="label">Your contact email address</span>
                        <abbr class="required" title="(required)">*</abbr></label>
                      <input id="applicantPreferredContactEmail" v-model="form.applicantPreferredContactEmail"
                        name="attributes.applicantPreferredContactEmail" type="email" required="required"
                        class="stretchWidth" maxlength="255">
                    </li>
                    <li class="applicantPreferredContactMobile">
                      <label for="applicantPreferredContactMobile"> <span class="label">Your contact phone / mobile
                        </span> <abbr class="required" title="(required)">*</abbr></label>
                      <input id="applicantPreferredContactMobile" v-model="form.applicantPreferredContactMobile"
                        name="attributes.applicantPreferredContactMobile" type="tel" pattern=".{10,}" required="required"
                        class="stretchWidth" maxlength="15">
                    </li>
                  </ul>
                </fieldset>
              </li>

              <li id="uploading">
                <p v-if="uploads.uploading > 0">Uploading your attachment... ({{ uploads.uploading }} remaining)</p>
                <p v-else></p>
              </li>
              <li id="inProgress">
                <p v-if="inProgress">Submitting your application...</p>
                <p v-else> </p>
              </li>
              <li>
                <fieldset>
                  <ul class="actions">
                    <li>
                      <strong><button :disabled="uploads.uploading > 0 || inProgress" id="submitButton">Submit
                          application</button></strong>
                    </li>
                  </ul>
                </fieldset>
              </li>
            </ol>
          </fieldset>
        </li>
      </ol>
    </form>
    <div v-if="eligibility.hasPoi == 'false' && form.personWhoTheSearchIsFor != 'no'" id="alert-poi" class="status warn">
      <span class="h2">You are not eligible</span>
      <br />
      <br />
      <p>We can't provide a letter of no record of marriage in Queensland until you can prove your identity.</p>
    </div>
    <div v-if="form.personWhoTheSearchIsFor == 'no'" id="alert-search-person" class="status warn">
      <span class="h2">You are not eligible</span>
      <br />
      <br />
      <p>We can't provide a letter of no record of marriage in Queensland until you have <a class="help lightbox"
          href="https://www.qld.gov.au/law/births-deaths-marriages-and-divorces/birth-death-and-marriage-certificates/eligibility#marriage-certificate-eligibility"
          id="lightbox-uid-3">authority</a> and <a class="help lightbox"
          href="https://www.qld.gov.au/law/births-deaths-marriages-and-divorces/requirements#proof-of-id"
          id="lightbox-uid-4">proof of ID</a> from the person who the search is for.</p>
    </div>
    <div id="privacy-notice">
      <strong>Privacy notice</strong>
      <br>
      The Department of Justice is collecting your personal information for the purpose of processing
      your no record of marriage application under the
      <i>Births, Deaths and Marriages Registration Act 2003</i>. Unless required or authorised by law, your personal
      information will not be provided to any other third party without your consent.
    </div>
    <hr />
  </div>
</template>

<script>
import sppapi from '@/sppapi.js';

export default {
  name: 'TheNoRecordOfMarriageForm',
  data: function () {
    return {
      uploads: {
        uploadIds: [],
        uploading: 0,
        acceptedTypes: '.jpg, .tiff, .png, .jpeg, .pdf',
      },
      processId: 'orderNoRecordOfMarriage',
      form: {
        applicantName: '',
        applicantPreferredContactMobile: '',
        applicantPreferredContactEmail: ''
      },
      eligibility: {},
      contactPhone: '',
      contactEmail: '',
      inProgress: false,
    }
  },
  computed: {

  },
  methods: {
    removeUpload: function (event) {
      event.preventDefault()
      let targetUploadId = event.target.attributes['targetUpload'].value
      document.getElementById(targetUploadId).value = ''
      this.uploads.uploadIds = this.uploadIds.filter(uploadId => uploadId.fieldId !== targetUploadId)
      return false
    },
    uploadExists: function (fieldId) {
      return this.uploads.uploadIds.filter(uploadId => uploadId.fieldId == fieldId).length
    },
    handleUpload: function (event) {
      const selection = event.target.files[0]
      const ext = selection.name.split('.').pop();
      if (this.uploads.acceptedTypes.indexOf(ext) < 0) {
        document.getElementById(event.target.id).value = ''
        return
      }

      this.uploading++
      const reader = new FileReader()
      reader.onload = (e) => {
        sppapi.uploadFile(this, selection.name, event.srcElement.name, e.target.result)
      }
      reader.readAsDataURL(selection)
    },
    submitForm: function (event) {
      event.preventDefault();
      this.contactPhone = this.form.applicantPreferredContactMobile;
      this.contactEmail = this.form.applicantPreferredContactEmail;

      if (this.uploads.uploadIds.length == 0) {
        return
      }
      if (this.form.applicantName == '' || this.form.applicantPreferredContactEmail == '' || this.form.applicantPreferredContactMobile == '') {
        return
      }

      sppapi.startProcessAndCompleteTask(this);
    }
  },
  components: {
  }
}

</script>

<style scoped lang="scss">
#inProgress {
  height: 1em;
}

#uploading {
  height: 1em;
}

.applicantPreferredContactEmail {
  width: 100%;
}

#supportingDocumentationFiles td {
  padding-bottom: 0.5em;
}

.removeUpload {
  cursor: pointer;
}
</style>
